<script setup>
import { computed, onBeforeUnmount, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useNuxtApp } from '#app'
import { useAuthStore } from '@/store/auth'
import { focusSiteMain } from '@/portal/accessibility.mjs'
import { academyColor, academyFetch, academyPath, loginLink, validSlug } from '@/utils/academy.mjs'
const props = defineProps({ title: { type: String, default: '' } })
const route = useRoute(), app = useNuxtApp(), auth = useAuthStore()
const academy = ref(null), error = ref(''), busy = ref(false), slug = computed(() => String(route.params.slug || ''))
const color = computed(() => academyColor(academy.value?.mainColor))
const home = computed(() => academyPath(slug.value)), my = computed(() => academyPath(slug.value, '/my'))
useHead(() => ({ title: `${props.title ? `${props.title} · ` : ''}${academy.value?.siteNm || '기업 연수원 안내'} · TECHEDU` }))
let epoch = 0, abort
onBeforeUnmount(() => { epoch++; abort?.abort(); academy.value = null })
async function load() {
  const current = ++epoch; abort?.abort(); const controller = new AbortController(); abort = controller
  academy.value = null; error.value = ''; busy.value = true
  const timer = setTimeout(() => controller.abort(), 15000)
  try {
    if (!validSlug(slug.value)) { const missing = new Error(); missing.status = 404; throw missing }
    // 공개 안내에 로그인 토큰을 보내지 않는다. 정확한 slug 하나만 서버에서 해석한다.
    const data = await academyFetch(`/api/academy/${encodeURIComponent(slug.value)}`, { signal: controller.signal })
    if (current === epoch) { academy.value = data; void app.$collectVisit?.(route, { siteSlug: slug.value }) }
  } catch (cause) { if (current === epoch) error.value = cause.name === 'AbortError' ? '연결 시간이 초과되었습니다. 다시 시도해 주세요.' : cause.status === 404 ? '공개된 연수원을 찾을 수 없습니다.' : '연수원 정보를 불러오지 못했습니다.' }
  finally { clearTimeout(timer); if (current === epoch) busy.value = false }
}
watch(slug, load, { immediate: true })
</script>
<template>
  <div class="academy-shell" :style="{ '--brand-color': color }">
    <a class="site-skip" href="#academy-main" @click.prevent="focusSiteMain({ scroll: true })">본문 바로가기</a>
    <header class="academy-top">
      <div class="academy-wrap academy-top-inner">
        <NuxtLink :to="home" class="academy-brand"><img v-if="academy?.logoUrl" class="academy-brand-logo" :src="academy.logoUrl" alt=""><span v-else class="academy-brand-mark" aria-hidden="true"><v-icon icon="mdi-school-outline" size="22" /></span><strong>{{ academy?.siteNm || '기업 연수원' }}</strong></NuxtLink>
        <nav class="academy-nav" aria-label="연수원 메뉴">
          <NuxtLink :to="home" :aria-current="route.path === home ? 'page' : undefined">교육과정</NuxtLink>
          <NuxtLink :to="my" :aria-current="route.path === my ? 'page' : undefined">내 강의실</NuxtLink>
          <NuxtLink v-if="!auth.isLoggedIn" class="academy-nav-login" :to="loginLink(home)">로그인</NuxtLink>
        </nav>
      </div>
    </header>
    <main id="academy-main" class="academy-wrap" tabindex="-1">
      <v-progress-linear v-if="busy" indeterminate color="primary" aria-label="연수원 조회 중" />
      <section v-if="error" class="academy-panel academy-state"><h1>연수원 안내</h1><p role="alert">{{ error }}</p><div class="academy-actions"><v-btn variant="outlined" :loading="busy" @click="load">다시 확인</v-btn><v-btn variant="text" to="/">TECHEDU 홈</v-btn></div></section>
      <slot v-if="academy" :academy="academy" :slug="slug" />
    </main>
    <footer class="academy-wrap academy-footer"><strong>{{ academy?.siteNm || '기업 연수원' }}</strong><span>TECHEDU 기업 연수원 · 기업의 배움과 성장을 함께합니다.</span><NuxtLink to="/">TECHEDU 홈</NuxtLink></footer>
  </div>
</template>
<style>
/* 연수원 화면 공통 스타일. 다른 화면과 겹치지 않도록 academy- 접두어만 사용한다. */
.academy-shell{min-height:100vh;background:#f6f8fb;color:#1e3044;border-top:5px solid var(--brand-color)}
.academy-shell a{color:inherit;text-decoration:none}
.academy-wrap{width:min(1120px,calc(100% - 64px));margin:0 auto}
.academy-top{background:#fff;border-bottom:1px solid #e1e7ee}
.academy-top-inner{min-height:76px;display:flex;justify-content:space-between;align-items:center;gap:20px}
.academy-brand{display:flex;align-items:center;gap:12px;min-width:0;font-size:19px;font-weight:800}
.academy-brand strong{overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
.academy-brand-logo{max-height:40px;max-width:150px;object-fit:contain}
.academy-brand-mark{width:38px;height:38px;flex-shrink:0;border-radius:10px;display:grid;place-items:center;background:var(--brand-color);color:#fff}
.academy-nav{display:flex;align-items:center;gap:6px;flex-shrink:0}
.academy-nav a{font-size:14px;font-weight:700;padding:8px 12px;border-radius:8px;color:#405366}
.academy-nav a[aria-current=page]{color:var(--brand-color)}
.academy-nav a:hover{background:#f1f5f8}
.academy-nav .academy-nav-login{background:var(--brand-color);color:#fff}
.academy-nav .academy-nav-login:hover{background:var(--brand-color);opacity:.9}
#academy-main{padding:32px 0 24px;outline:none}
.academy-panel{background:#fff;border:1px solid #e1e7ee;border-radius:14px;padding:32px;min-width:0;margin-bottom:24px}
.academy-panel h1{font-size:30px;line-height:1.35;letter-spacing:-.8px;margin:0 0 14px;overflow-wrap:anywhere}
.academy-panel h2{font-size:21px;margin:0 0 16px}
.academy-panel p{font-size:14px;line-height:1.8;color:#526579}
.academy-state p{margin:0 0 20px}
.academy-actions{display:flex;flex-wrap:wrap;gap:10px;align-items:center}
.academy-chip{display:inline-flex;align-items:center;font-size:12px;font-weight:700;padding:3px 10px;border-radius:999px;background:#eef3f6;color:#405366}
.academy-chip.on{background:color-mix(in srgb,var(--brand-color) 14%,#fff);color:var(--brand-color)}
.academy-muted{color:#64748b;font-size:13px}
.academy-pager{display:flex;justify-content:center;align-items:center;gap:12px;margin-top:22px}
.academy-footer{display:flex;flex-wrap:wrap;gap:8px 18px;align-items:center;padding:24px 0 40px;font-size:12px;color:#64748b}
.academy-footer strong{color:#1e3044}
.academy-footer a{margin-left:auto;font-weight:700}
.academy-shell .v-btn{max-width:100%}
.academy-shell .v-btn__content{white-space:normal}
@media(max-width:800px){.academy-wrap{width:calc(100% - 32px)}.academy-top-inner{flex-wrap:wrap;padding:12px 0;min-height:0}.academy-nav{width:100%;justify-content:flex-end}.academy-panel{padding:22px}.academy-panel h1{font-size:25px}.academy-footer a{margin-left:0}}
</style>
